<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// public:true HANYA menghilangkan halangan router guard TENANT (lihat
// guards.js). Keamanan sesungguhnya halaman ini ada di dua tempat lain:
//  1. Pengecekan cookie 'platformAccessToken' di bawah (redirect manual
//     kalau kosong) — lapisan frontend.
//  2. Middleware auth:platform di backend pada endpoint yang dipanggil
//     halaman ini — lapisan sesungguhnya, karena tanpa token platform
//     admin valid tidak ada data yang bisa diambil sama sekali.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

// ─── State ────────────────────────────────────────────────────────────────
const tenants = ref([])
const loading = ref(false)
const search = ref('')
const page = ref(1)
const perPage = ref(20)
const totalItems = ref(0)

const headers = [
  { title: 'Nama', key: 'name', sortable: false },
  { title: 'Slug', key: 'slug', sortable: false },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'User', key: 'users_count', sortable: false, align: 'center' },
  { title: 'Status', key: 'is_active', sortable: false },
  { title: 'Dibuat', key: 'created_at', sortable: false },
  { title: 'Aksi', key: 'actions', sortable: false, align: 'center', width: 100 },
]

// Handle 401/403 dari endpoint manapun di halaman ini secara seragam: token
// platform admin sudah tidak valid (expired/direvoke) -> paksa login ulang.
const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

const fetchTenants = async () => {
  loading.value = true
  try {
    const query = { page: page.value, per_page: perPage.value }
    if (search.value)
      query.search = search.value

    const res = await usePlatformApi('/api/v1/platform/tenants', { query }).get()
    const body = res?.data ?? res

    tenants.value = body?.data ?? []
    totalItems.value = body?.pagination?.total ?? 0
  } catch (err) {
    if (await handleAuthError(err))
      return

    console.error('[Platform] fetch tenants error', err)
    tenants.value = []
  } finally {
    loading.value = false
  }
}

watch([search, page], fetchTenants, { immediate: true })

// ─── Delete / deactivate ────────────────────────────────────────────────────
const deleteDialog = ref(false)
const selectedTenant = ref(null)
const deleteLoading = ref(false)
const deleteError = ref('')

const confirmDelete = tenant => {
  selectedTenant.value = tenant
  deleteError.value = ''
  deleteDialog.value = true
}

const doDelete = async () => {
  deleteLoading.value = true
  deleteError.value = ''
  try {
    const res = await usePlatformApi(`/api/v1/platform/tenants/${selectedTenant.value.id}`).delete()
    const body = res?.data ?? res

    deleteDialog.value = false

    // Backend membalas pesan yang beda tergantung tenant sudah punya user
    // atau belum (dihapus permanen vs dinonaktifkan) — cukup teruskan apa
    // adanya lewat notifikasi ringan, tidak perlu dibedakan di frontend.
    console.info('[Platform]', body?.message)
    fetchTenants()
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    deleteError.value = data?.message ?? 'Gagal menghapus tenant.'
    console.error('[Platform] delete tenant error', err)
  } finally {
    deleteLoading.value = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4">
      <div>
        <h4 class="text-h4">
          Daftar Tenant
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>

      <VBtn
        color="primary"
        :to="{ path: '/platform/tenants/create' }"
      >
        Tambah Tenant
      </VBtn>
    </div>

    <VCard>
      <VCardText class="d-flex align-center">
        <VTextField
          v-model="search"
          label="Cari nama tenant"
          density="compact"
          style="max-width: 320px;"
          clearable
        />
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:page="page"
        :headers="headers"
        :items="tenants"
        :items-length="totalItems"
        :loading="loading"
        :items-per-page="perPage"
        no-data-text="Belum ada tenant."
        @update:options="fetchTenants"
      >
        <template #item.is_active="{ item }">
          <VChip
            :color="item.is_active ? 'success' : 'secondary'"
            size="small"
          >
            {{ item.is_active ? 'Aktif' : 'Nonaktif' }}
          </VChip>
        </template>

        <template #item.created_at="{ item }">
          {{ new Date(item.created_at).toLocaleDateString('id-ID') }}
        </template>

        <template #item.actions="{ item }">
          <div class="d-flex gap-1 justify-center">
            <IconBtn :to="{ path: `/platform/tenants/${item.id}/edit` }">
              <VIcon icon="ri-pencil-line" />
              <VTooltip activator="parent">
                Edit
              </VTooltip>
            </IconBtn>
            <IconBtn
              color="error"
              @click="confirmDelete(item)"
            >
              <VIcon icon="ri-delete-bin-line" />
              <VTooltip activator="parent">
                Hapus
              </VTooltip>
            </IconBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>

  <!-- Confirm Delete Dialog -->
  <VDialog
    v-model="deleteDialog"
    max-width="440"
    persistent
  >
    <VCard>
      <VCardTitle class="pt-5 px-5">
        Hapus Tenant?
      </VCardTitle>
      <VCardText class="px-5">
        Yakin ingin menghapus <strong>{{ selectedTenant?.name }}</strong>?
        <br>
        <span
          v-if="selectedTenant?.users_count > 0"
          class="text-caption text-medium-emphasis"
        >
          Tenant ini sudah punya {{ selectedTenant.users_count }} user, jadi TIDAK akan dihapus
          permanen — sistem akan menonaktifkannya saja supaya data staf/transaksi tetap aman.
        </span>
        <span
          v-else
          class="text-caption text-medium-emphasis"
        >
          Tenant ini belum punya user sama sekali, jadi akan dihapus permanen.
        </span>

        <VAlert
          v-if="deleteError"
          type="error"
          variant="tonal"
          density="compact"
          class="mt-3"
        >
          {{ deleteError }}
        </VAlert>
      </VCardText>
      <VCardActions class="px-5 pb-5">
        <VSpacer />
        <VBtn
          variant="tonal"
          @click="deleteDialog = false"
        >
          Batal
        </VBtn>
        <VBtn
          color="error"
          :loading="deleteLoading"
          @click="doDelete"
        >
          Hapus
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
